/**
 * Converts a decoded ID token plus the current GROUPS config into the User shape
 * the rest of the app expects. We isolate this so AuthContext doesn’t need to
 * know about how features are mapped or how AWS principal tags are structured.
 */
import type { User } from '@/services/auth-service';
import type { GroupsConfig } from '@/config/app-config';

export type DecodedToken = Record<string, unknown> & { exp?: number; sub?: string };

export const buildUserFromToken = (decodedId: DecodedToken, groupsCfg: GroupsConfig): User => {
  const awsTags = decodedId?.['https://aws.amazon.com/tags'] as { principal_tags?: { Groups?: string[] } } | undefined;
  const groups = (awsTags?.principal_tags?.Groups ?? []) as unknown;
  const normalizedGroups = Array.isArray(groups) ? (groups as string[]) : [];

  const features = new Set<string>();
  for (const group of normalizedGroups) {
    const groupFeatures = groupsCfg?.[group]?.features;
    if (Array.isArray(groupFeatures)) {
      for (const feature of groupFeatures) {
        features.add(feature);
      }
    }
  }

  return {
    email:
      (decodedId['email'] as string) ||
      (decodedId['cognito:username'] as string) ||
      (decodedId['username'] as string) ||
      '',
    groups: normalizedGroups,
    features: Array.from(features),
    sub: decodedId['sub'],
  };
};
